import {
  Component,
  ElementRef,
  EventEmitter,
  Inject,
  Input,
  OnInit,
  OnChanges,
  Output,
  SimpleChanges,
  TemplateRef,
  ViewChild,
  AfterViewInit,
  ViewChildren,
  QueryList,
  OnDestroy,
} from '@angular/core';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { finalize, map, Observable, Subscription, take, timer } from 'rxjs';
import { NgOtpInputComponent } from 'ng-otp-input';
import { TranslateService } from '@ngx-translate/core';
import {
  FormlyFieldConfig,
  FormlyFormBuilder,
  FormlyFormOptions,
} from '@ngx-formly/core';
import { DOCUMENT } from '@angular/common';
import { ICertificateDownload } from '../../models/certificate-download.model';
import {
  IApplicationCertificate,
  IRequestAccessResponse,
} from '../../models/track-application.model';
import { IApplicationFormData } from '../../models/application-form-data.model';
import { IEnvironment } from '../../models/environment.model';
import { ILocale } from '../../models/locale.model';
import { IServiceTranslation } from '../../models/service-translation.model';
import { ToastService } from '../../services/toast/toast.service';
import { PublicServicesService } from '../../services/public-services/public-services.service';
import { PaymentService } from '../../services/public-services/payment.service';
import { ApplicationsService } from '../../services/application/applications.service';
import { ServiceTranslationMergerService } from '../../services/service-translation-merger/service-translation-merger.service';
import {
  IFeedbackResponseDetails,
  IOfficerAttachment,
  IUnifiedDocument,
} from '../../models/application-details.model';
import { applicationFormBuildPreviewDataTranslated } from '../../../utils/utils/application-form-translated.util';
import { IApplication } from '../../models/application.model';
import {
  getApplicationStateColor,
  getApplicationStateLabel,
} from '../../services/application/application.service';
import { LocaleService } from '../../services/locale/locale.service';
import { Gov2SummaryService } from '../../../gov2/services/gov2-summary.service';
import { Details } from '../../../gov2/models/summary.model';
import { extractErrorMessage as extractBackendErrorMessage } from '../../../gov2/utils/gov2-error.util';
import { performFormTranslations } from '../../../utils/utils/form-translations.util';
import { FormGroup } from '@angular/forms';
import { EApplicationSource } from '../../../gov2/enums/application-source.enum';
import { IFederatedApplicationBase } from '../../../gov2/models/federated-application.model';
import getUnicodeFlagIcon from 'country-flag-icons/unicode';

declare const IremboPay: any;

@Component({
  standalone: false,
  selector: 'irembogov-application-details',
  templateUrl: './application-details.component.html',
  styleUrls: ['./application-details.component.scss'],
})
export class ApplicationDetailsComponent
  implements OnInit, AfterViewInit, OnChanges, OnDestroy
{
  @Input() application!: any;
  @Input() showBackButton = true;
  @Output() backEvent = new EventEmitter<void>();
  @Output() editApplicationEvent = new EventEmitter<string>();
  @Input() isLoggedIn = false;

  // Gov2 (Legacy) Application Support
  @Input() isLegacyApplication = false;
  @Input() federatedApplication: IFederatedApplicationBase | undefined;
  @Input() gov2SummaryId: string | undefined;

  private readonly environment: IEnvironment;

  showFullDetails = false;
  requestingAccess = false;
  otp = '';
  encodedOtp = '';
  isConfirming = false;
  invalidToken = false;
  requestAccessResponse: IRequestAccessResponse | undefined;
  countdown$: Observable<number> | undefined;
  countdownTime = '00:00';
  minutes = 5;
  countdownSubscription: Subscription | undefined;
  resendingToken = false;
  locale = '';
  loadingChildServices = false;

  isApplicationLoading = false;
  applicationNotFound = false;
  isGettingReceiptLoading = false;
  captureSearchOption!: string | undefined;
  isDownloadingCertificate = false;
  @ViewChild('applicationResults') applicationResults: ElementRef | undefined;
  @ViewChild('token') tokenModal!: TemplateRef<unknown>;
  @ViewChildren('certificateDropdown')
  certificateDropdowns!: QueryList<ElementRef>;
  applicationSummary: IApplicationFormData[] | undefined;
  applicationBaseUrl = '';
  applicationPath = '/application/v1/file-manager/application/attachment';
  viewMoreDetails = false;
  viewFullDetails = false;
  tokenResent = false;
  downloadingCert = false;
  downloadingReciept = false;
  selectedPanel: number | undefined;
  supportedLocales: ILocale[] = [];
  selectedCertLocale: string | undefined;
  hideSearchOnMobile: boolean | undefined;
  serviceTranslations?: IServiceTranslation;
  isLoadingTranslations = false;
  applicationDetails: Record<string, any> | undefined;
  applicationDetailsFetched = false;
  showDropdown = true;
  downloadingReceipt = false;
  downloadedAttachment: ICertificateDownload | undefined;

  private subscriptions = new Subscription();

  onlinePaymentActivated = false;
  paymentComplete = false;
  billId: string | undefined = '';

  // Gov2 Summary Data
  gov2Summary: Details[] = [];
  gov2Documents: any[] = [];
  isLoadingGov2Summary = false;
  gov2SummaryLoaded = false;
  receiptSupportedLocales: ILocale[] = [];
  @ViewChildren('receiptDropdown') receiptDropdowns!: QueryList<ElementRef>;
  showReceiptDownloadButton = false;

  constructor(
    private applicationService: ApplicationsService,
    private modalService: NgbModal,
    private ts: ToastService,
    private localeService: LocaleService,
    private publicServicesService: PublicServicesService,
    private translateService: TranslateService,
    private applicationsService: ApplicationsService,
    @Inject(DOCUMENT) private _document: Document,
    private paymentService: PaymentService,
    readonly serviceTranslationMerger: ServiceTranslationMergerService,
    @Inject('environment') environment: IEnvironment,
    private formlyBuilder: FormlyFormBuilder,
    private gov2SummaryService: Gov2SummaryService
  ) {
    this.environment = environment;
    this.applicationBaseUrl = this.environment.apiGatewayBaseUrl;
  }

  ngOnInit(): void {
    this.locale = localStorage.getItem('locale') || 'en-US';
    this.billId = this.application.billId;
    // Fetch the locales for the receipt dropdown immediately
    this.getReceiptSupportedLocales();

    if (!this.showBackButton) {
      // wait for the view to render
      setTimeout(() => {
        document.getElementById('viewMoreDetails')?.click();
      }, 250);

      if (this.isJourney() === false) {
        this.requestingAccess = true;
        this.viewMoreDetails = true;
        this.getDetails(this.application);
      }
    }
    this.setPaymentV2Script();
  }

  ngAfterViewInit(): void {
    // Initialize dropdowns after view initialization
    setTimeout(() => {
      this.initializeDropdowns();
    }, 0);
  }

  ngOnChanges(changes: SimpleChanges): void {
    // Reset Gov2 state when summaryId changes (user will need to click "View More Details" to load)
    if (changes['gov2SummaryId'] && this.isLegacyApplication) {
      this.gov2SummaryLoaded = false;
      this.gov2Summary = [];
      this.gov2Documents = [];
    }
  }

  private initializeDropdowns(): void {
    // Check if Bootstrap is available and initialize dropdowns
    if (typeof window !== 'undefined' && (window as any).bootstrap) {
      const bootstrap = (window as any).bootstrap;
      this.certificateDropdowns.forEach(dropdown => {
        new bootstrap.Dropdown(dropdown.nativeElement);
      });
      // Initialize Receipt Dropdowns
      this.receiptDropdowns.forEach(dropdown => {
        new bootstrap.Dropdown(dropdown.nativeElement);
      });
    }
  }

  getApplicationStatusToColor(applicationStatus: string): string {
    return getApplicationStateColor(applicationStatus);
  }

  changeApplicationStatusToLabel(applicationStatus: string): string {
    return getApplicationStateLabel(applicationStatus);
  }

  goBack() {
    this.backEvent.emit();
  }

  openModel(
    content: unknown,
    windowClass: string,
    size?: string,
    backdrop?: string
  ) {
    let options: Record<string, unknown> = {
      ariaLabelledBy: 'confirm-otp',
      windowClass,
      centered: true,
      size,
    };
    if (backdrop) {
      options = { ...options, backdrop };
    }
    return this.modalService.open(content, options);
  }

  handleViewMoreDetails(content: TemplateRef<unknown>) {
    if (
      (this.isJourney() || !this.showBackButton) &&
      this.viewFullDetails === false
    ) {
      this.viewMoreDetails = true;
    } else {
      this.requestingAccess = true;
      this.subscriptions.add(
        this.applicationService
          .requestAccessByApplicationNumber(
            this.application.applicationNumber,
            this.locale
          )
          .subscribe({
            next: res => {
              if (
                res.responseCode === 'APPLICATION_OTP_NOT_REQUIRED' &&
                this.application
              ) {
                this.getDetails(this.application);
                // If edit was pending, OTP is not required — proceed
                if (this.pendingEditAfterOtp) {
                  this.pendingEditAfterOtp = false;
                  this.editApplicationEvent.emit('');
                }
              } else {
                this.openOTPModal(content, res.data);
              }
            },
            error: () => {
              this.requestingAccess = false;
              this.showErrorToastMessage();
            },
          })
      );
    }
  }

  handleChildOtpRequest(applicationNumber: string): void {
    void applicationNumber;
    // Check if we already have OTP
    if (this.otp && this.otp !== '') {
      return;
    }

    this.requestingAccess = true;
    this.subscriptions.add(
      this.applicationService
        .requestAccessByApplicationNumber(
          this.application.applicationNumber, // ← Use PARENT's app number - OTP works for all children
          this.locale
        )
        .subscribe({
          next: res => {
            if (res.responseCode === 'APPLICATION_OTP_NOT_REQUIRED') {
              // OTP not required - signal child with empty OTP to proceed
              this.otp = ''; // Empty string signals "no OTP needed, proceed"
              this.requestingAccess = false;
            } else {
              // OTP required - show modal
              this.openOTPModal(this.tokenModal, res.data);
            }
          },
          error: () => {
            this.requestingAccess = false;
            this.showErrorToastMessage();
          },
        })
    );
  }

  downloadReceiptbyOtp(applicationNumber: string, locale?: string) {
    this.downloadingReciept = true;
    const downlaodName = `${applicationNumber}_payment_receipt`;
    const targetLocale = locale || this.locale;
    this.subscriptions.add(
      this.applicationService
        .getPaymentReceiptByOTP(applicationNumber, this.otp, targetLocale)
        .subscribe({
          next: res => {
            this.downloadingReciept = false;
            this.handleFiledownload(
              res.data.file,
              res.data.contentType,
              downlaodName
            );
          },
          error: () => {
            this.downloadingReciept = false;
            this.ts.show({
              body: 'Error has occurred',
              type: 'error',
              delay: 5000,
            });
          },
        })
    );
  }

  opendownloadCertificateModal(content: TemplateRef<unknown>) {
    this.modalService.dismissAll();
    this.openModel(content, 'download-cert-modal', undefined, 'static');
  }

  downloadCertificate(locale?: string) {
    if (locale) {
      this.downloadingCert = true;
      const downloadName = `${this.application.serviceName} - ${this.application.applicationNumber}`;

      if (this.isLegacyApplication) {
        this.downloadGov2Certificate(locale, downloadName);
        return;
      }

      this.subscriptions.add(
        this.applicationService
          .getCertificateByOTP(
            this.application.applicationNumber,
            this.otp,
            locale
          )
          .subscribe({
            next: res => {
              this.downloadingCert = false;
              this.handleFiledownload(
                res.data.file,
                res.data.contentType,
                downloadName
              );
              this.modalService.dismissAll();
              this.selectedCertLocale = undefined;
            },
            error: () => {
              this.downloadingCert = false;
              this.showErrorToastMessage();
            },
          })
      );
    }
  }

  private downloadGov2Certificate(language: string, downloadName: string) {
    this.subscriptions.add(
      this.applicationService
        .downloadGov2Certificate(this.application.applicationNumber, language)
        .subscribe({
          next: (res: any) => {
            this.downloadingCert = false;
            const data = res.data;
            this.handleFiledownload(data, 'application/pdf', downloadName);
            this.modalService.dismissAll();
            this.selectedCertLocale = undefined;
          },
          error: () => {
            this.downloadingCert = false;
            this.showErrorToastMessage();
          },
        })
    );
  }

  onOtpChange(event: string, ngOtpInputRef: NgOtpInputComponent) {
    this.otp = event;
    this.encodedOtp = btoa(this.otp);
    if (ngOtpInputRef) {
      const controls = ngOtpInputRef.otpForm.controls;
      for (const key in controls) {
        if (Object.prototype.hasOwnProperty.call(controls, key)) {
          const element = controls[key];
          if (!element.value) {
            element.reset();
          }
        }
      }
    }
  }

  confirm(ngOtpInputRef: NgOtpInputComponent) {
    if (this.otp.length === 5) {
      this.isConfirming = true;
      this.getDetailsByOTP(this.application, ngOtpInputRef);
    }
  }

  private markControlsAsInValid(ngOtpInputRef: NgOtpInputComponent) {
    if (ngOtpInputRef) {
      this.invalidToken = true;
      const controls = ngOtpInputRef.otpForm.controls;
      for (const key in controls) {
        if (Object.prototype.hasOwnProperty.call(controls, key)) {
          const element = controls[key];
          element.setErrors({ invalidToken: true });
        }
      }
    }
  }

  startCountdown() {
    const totalSeconds = this.minutes * 60;
    this.countdown$ = timer(0, 1000).pipe(
      take(totalSeconds + 1),
      map(elapsed => totalSeconds - elapsed)
    );

    this.countdownSubscription = this.countdown$.subscribe(seconds => {
      this.countdownTime = this.formatTime(seconds);
    });
  }

  formatTime(totalSeconds: number): string {
    const minutes: number = Math.floor(totalSeconds / 60);
    const seconds: number = totalSeconds % 60;
    return `${this.pad(minutes)}:${this.pad(seconds)}`;
  }

  pad(value: number): string {
    return value < 10 ? `0${value}` : value.toString();
  }

  restartCountdown() {
    this.countdownSubscription?.unsubscribe();
    this.startCountdown();
  }

  resendOTP() {
    this.resendingToken = true;
    this.subscriptions.add(
      this.applicationService
        .requestAccessByApplicationNumber(
          this.application.applicationNumber,
          this.locale
        )
        .subscribe({
          next: () => {
            this.resendingToken = false;
            this.restartCountdown();
          },
          error: () => {
            this.resendingToken = false;
            this.ts.show({
              body: 'Error resending OTP',
              type: 'error',
              delay: 5000,
            });
          },
        })
    );
  }

  handleFiledownload(
    base64Data: string,
    contentType: string,
    fileName: string
  ) {
    const blob = this.base64ToBlob(base64Data, contentType);
    this.downloadBlob(fileName, blob);
  }

  private downloadBlob(fileName: string, blob: Blob): void {
    const anchor = window.document.createElement('a');
    anchor.href = window.URL.createObjectURL(blob);
    anchor.download = fileName;
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
    window.URL.revokeObjectURL(anchor.href);
  }

  private base64ToBlob(str: string, type: string) {
    const content = atob(str);
    const buffer = new ArrayBuffer(content.length);
    const view = new Uint8Array(buffer);
    for (let n = 0; n < content.length; n++) {
      view[n] = content.charCodeAt(n);
    }
    const blob = new Blob([buffer], { type: type });
    return blob;
  }

  expandPanel(index: number) {
    this.selectedPanel = index;
  }

  openOTPModal(content: TemplateRef<unknown>, data: IRequestAccessResponse) {
    this.requestingAccess = false;
    this.invalidToken = false;
    this.otp = '';
    this.modalService.dismissAll();
    this.openModel(
      content,
      'token-input-modal',
      undefined,
      'static'
    ).result.then(
      () => {
        this.countdownSubscription?.unsubscribe();
      },
      () => {
        this.countdownSubscription?.unsubscribe();
      }
    );
    this.requestAccessResponse = data;
    this.startCountdown();
  }

  getDetailsByOTP(
    application: IApplication,
    ngOtpInputRef: NgOtpInputComponent
  ) {
    this.applicationDetailsFetched = false;
    this.subscriptions.add(
      this.applicationService
        .getApplicationDetailsByOTP(application.applicationNumber, this.otp)
        .subscribe({
          next: res => {
            this.modalService.dismissAll();
            this.applicationDetails = res.data;
            this.applicationDetailsFetched = true;
            this.getServiceTranslation(this.locale);
            this.viewMoreDetails = true;
            this.showReceiptDownloadButton = true;

            // For Journey V3, don't show parent summary - only show child details
            // For Journey V2 and other types, show parent summary
            if (application.applicationType === 'JOURNEY_V3') {
              this.viewFullDetails = false;
            } else {
              this.viewFullDetails = true;
            }

            this.isConfirming = false;
            if (
              Object.prototype.hasOwnProperty.call(
                this.applicationDetails,
                'applicationCertificate'
              ) &&
              this.applicationDetails['applicationCertificate']
            ) {
              this.getSupportedLocales();
            }

            // If edit was pending OTP verification, emit now
            if (this.pendingEditAfterOtp) {
              this.pendingEditAfterOtp = false;
              this.editApplicationEvent.emit(this.otp);
            }
          },
          error: () => {
            this.markControlsAsInValid(ngOtpInputRef);
            this.isConfirming = false;
          },
        })
    );
  }

  getJourneyDetails(content: TemplateRef<unknown>) {
    if (this.viewFullDetails) {
      this.viewFullDetails = false;
      return;
    }
    this.viewFullDetails = true;

    if (this.applicationSummary === undefined)
      this.handleViewMoreDetails(content);
  }

  getDetails(application: IApplication) {
    this.applicationDetailsFetched = false;
    if (this.showBackButton)
      this.subscriptions.add(
        this.applicationService
          .getApplicationDetailsByOTP(application.applicationNumber, '')
          .subscribe({
            next: res => {
              this.applicationDetails = res.data;
              this.getServiceTranslation(this.locale);
              this.viewMoreDetails = true;
              this.viewFullDetails = true;
              this.applicationDetailsFetched = true;
              if (
                Object.prototype.hasOwnProperty.call(
                  this.applicationDetails,
                  'applicationCertificate'
                ) &&
                this.applicationDetails['applicationCertificate']
              ) {
                this.getSupportedLocales();
              }
            },
            error: () => {
              this.showErrorToastMessage();
              this.requestingAccess = false;
            },
          })
      );
    else
      this.subscriptions.add(
        this.applicationService
          .getApplicationDetailsById(application.applicationId)
          .subscribe({
            next: res => {
              this.applicationDetails = res;
              this.applicationDetailsFetched = true;
              this.getServiceTranslation(this.locale);
              this.viewMoreDetails = true;
              this.viewFullDetails = true;
              if (
                Object.prototype.hasOwnProperty.call(
                  this.applicationDetails,
                  'applicationCertificate'
                ) &&
                this.applicationDetails?.['applicationCertificate']
              ) {
                this.getSupportedLocales();
              }

              if (this.isJourney() === false) {
                this.requestingAccess = true;
              }
            },
            error: () => {
              this.showErrorToastMessage();
              this.requestingAccess = false;
            },
          })
      );
  }

  private buildApplicationSummary(applicationDetails: Record<string, unknown>) {
    const applicationSummary = applicationDetails[
      'applicationSummary'
    ] as Record<string, unknown>;
    const formDefinition = applicationDetails['formDefinition'] as Record<
      string,
      unknown
    >;
    const fields = formDefinition['fields'] as FormlyFieldConfig[];

    if (!fields || !applicationSummary) {
      return;
    }

    // Translate first
    const translatedLayout = performFormTranslations(
      {
        translatedLayout: structuredClone(fields),
        layout: fields,
        serviceTranslations: this.serviceTranslations,
        translateService: this.translateService,
      },
      this.translateService.currentLang || 'en'
    );

    // Build a temporary Formly form to trigger all expression evaluations
    const form = new FormGroup({});
    const currentLang = this.translateService.currentLang || 'en';
    const options: FormlyFormOptions = {
      formState: {
        getTranslation: (key: string) => {
          const translated = this.translateService.instant(key);
          if (translated !== key) {
            return translated;
          }
          const localeTranslations = this.serviceTranslations?.[currentLang] as
            | Record<string, string>
            | undefined;
          return localeTranslations?.[key] || key;
        },
      },
    };

    // Build a temporary Formly form to trigger all expression evaluations
    try {
      this.formlyBuilder.buildForm(
        form,
        translatedLayout,
        applicationSummary,
        options
      );
    } catch (err) {
      console.warn('Formly buildForm failed (non-critical for preview):', err);
    }

    // Pass evaluated layout into your existing builder
    this.applicationSummary = applicationFormBuildPreviewDataTranslated(
      translatedLayout,
      applicationSummary,
      this.serviceTranslations,
      false,
      this.translateService
    );
  }

  showErrorToastMessage() {
    this.ts.show({
      body: 'Error has occurred, please try again',
      type: 'error',
      delay: 5000,
    });
  }

  getSupportedLocales() {
    this.localeService
      .getSupportedLocales()
      .pipe(
        map(res => {
          return this.filterLocales(res.data.content);
        })
      )
      .subscribe({
        next: response => {
          this.supportedLocales = response;
        },
        error: (error: any) => {
          this.ts.show({
            body: error.error.responseMessage ?? error.error.message,
            type: 'error',
            delay: 5000,
          });
        },
      });
  }

  getShortLocale(locale: string): string {
    if (locale === 'en-US') return 'gb';
    if (locale === 'sw') return 'tz';
    const shortLocale = locale.includes('-') ? locale.split('-')[1] : locale;
    return shortLocale.toLowerCase();
  }

  selectLocale(locale: string) {
    this.selectedCertLocale = locale;
  }

  private filterLocales(locales: ILocale[]): ILocale[] {
    if (this.applicationDetails) {
      const applicationCertificate = this.applicationDetails[
        'applicationCertificate'
      ] as IApplicationCertificate;
      const certificateLocales = Object.keys(
        applicationCertificate.certificateNames
      );
      locales = locales.filter(x => certificateLocales.includes(x.locale));
    }
    return locales;
  }

  getServiceTranslation(locale: string) {
    this.isLoadingTranslations = true;
    const serviceId = (this.applicationDetails?.['serviceId'] as string) ?? '';

    if (serviceId) {
      this.subscriptions.add(
        this.serviceTranslationMerger
          .loadAndMergeServiceTranslation(
            serviceId,
            locale,
            this.translateService
          )
          .subscribe({
            next: response => {
              this.isLoadingTranslations = false;
              this.serviceTranslations = response;

              if (this.applicationDetails) {
                this.buildApplicationSummary(this.applicationDetails);
              }

              this.requestingAccess = false;
            },
            error: () => {
              this.isLoadingTranslations = false;
              this.requestingAccess = false;
              if (this.applicationDetails) {
                this.buildApplicationSummary(this.applicationDetails);
              }
            },
          })
      );
    } else {
      this.applicationDetails &&
        this.buildApplicationSummary(this.applicationDetails);
    }
  }

  getPaymentPublicKey(merchant: string | undefined): Promise<any> {
    return new Promise((resolve, reject) => {
      if (merchant !== undefined) {
        this.subscriptions.add(
          this.applicationsService.getPaymentPublicKey(merchant).subscribe({
            next: res => {
              const publicKey = res.data.publicKey;
              resolve(publicKey);
            },
          })
        );
      } else {
        reject();
      }
    });
  }

  setPaymentV2Script(): void {
    if (this._document.getElementById('iremboPay')) {
      this.onlinePaymentActivated = true;
      return;
    }
    const iremboPayCdnScript = this._document.createElement('script');
    iremboPayCdnScript.id = 'iremboPay';
    iremboPayCdnScript.src = this.environment.IREMBOPAY_CDN ?? '';
    iremboPayCdnScript.type = 'text/javascript';
    document.getElementsByTagName('head')[0].appendChild(iremboPayCdnScript);
    iremboPayCdnScript.onload = () => {
      this.onlinePaymentActivated = true;
    };
  }

  makePayment() {
    // Check if legacy (Gov 2.0) application using enum
    if (
      this.federatedApplication?.source === EApplicationSource.IREMBO_GOV_2_0
    ) {
      this.getLegacyPaymentKey();
    } else {
      this.getHubPaymentKey();
    }
  }

  private getHubPaymentKey() {
    this.getPaymentPublicKey(this.application.merchant).then(publicKey => {
      if (publicKey) {
        this.openPaymentModal(publicKey);
      }
    });
  }

  private getLegacyPaymentKey() {
    this.applicationsService
      .getLegacyPaymentMerchantKey(this.application.merchant, this.locale)
      .subscribe({
        next: response => {
          if (response.data && response.data.publicKey) {
            this.openPaymentModal(response.data.publicKey);
          }
        },
        error: () => {
          this.ts.show({
            body: 'Error loading payment information',
            type: 'error',
            delay: 5000,
          });
        },
      });
  }

  private openPaymentModal(publicKey: string) {
    IremboPay.initiate({
      publicKey: publicKey,
      invoiceNumber: this.billId,
      locale: IremboPay.locale.EN,
      isTest: this.environment.IREMBOPAY_TEST,
      callback: (err: unknown, resp: Record<string, string>) => {
        if (!err) {
          IremboPay.closeModal();
          this.paymentComplete = true;
          this.ts.show({
            body: resp['message'],
            type: 'success',
          });
          this.getDetails(this.application);
        }
      },
    });
  }

  getLocale() {
    return this.locale;
  }

  isJourney() {
    return (
      !this.application.parentApplicationId &&
      (this.application.applicationType === 'JOURNEY' ||
        this.application.applicationType === 'JOURNEY_V2' ||
        this.application.applicationType === 'JOURNEY_V3')
    );
  }

  isSelectedApplicationFree(application: any): boolean {
    return (
      application?.priceType === 'FREE' ||
      (application?.priceType === 'DYNAMIC' && Number(application?.price) === 0)
    );
  }

  downloadReceipt(applicationNumber: string | undefined, locale: string) {
    if (applicationNumber !== undefined) {
      if (this.otp !== '') {
        this.downloadReceiptbyOtp(applicationNumber, locale);
      } else {
        this.downloadingReceipt = true;
        this.subscriptions.add(
          this.paymentService
            .getPaymentReceipt(applicationNumber, locale)
            .subscribe({
              next: res => {
                this.downloadingReceipt = false;
                const source =
                  `data:` + res?.contentType + `;base64,${res?.file}`;
                const link = document.createElement('a');
                link.href = source;
                link.download = applicationNumber + `_payment_receipt.pdf`;
                link.click();
              },
              error: () => {
                this.downloadingReceipt = false;
                this.ts.show({
                  body: 'Error downloading payment receipt',
                  type: 'error',
                });
              },
            })
        );
      }
    }
  }

  downloadAttachment(attachment: IOfficerAttachment) {
    if (this.otp !== '') {
      this.downloadAttachmentByOtp(attachment);
    } else {
      this.isDownloadingCertificate = true;
      this.subscriptions.add(
        this.publicServicesService
          .downloadAttachment(attachment.applicationId, attachment.fileName)
          .subscribe({
            next: res => {
              this.downloadedAttachment = {
                file: '',
                contentType:
                  res.headers.get('Content-Type') ?? 'application/pdf',
              };
              this.downloadBlob(
                attachment?.serviceName +
                  ' - ' +
                  attachment.applicationNumber +
                  attachment.fileName.split('.').pop(),
                res.body as Blob
              );
            },
            error: () => {
              this.ts.show({
                body: 'Error downloading attachment',
                type: 'error',
              });
              this.isDownloadingCertificate = false;
            },
          })
      );
    }
  }

  downloadAttachmentByOtp(attachment: IOfficerAttachment) {
    this.downloadingCert = true;
    this.subscriptions.add(
      this.applicationService
        .downloadAttachmentByOTP(
          attachment.applicationNumber,
          this.otp,
          attachment.fileName
        )
        .pipe(finalize(() => (this.downloadingCert = false)))
        .subscribe({
          next: res => {
            this.downloadedAttachment = {
              file: '',
              contentType: res.headers.get('Content-Type') ?? 'application/pdf',
            };
            this.downloadBlob(
              attachment?.serviceName +
                ' - ' +
                attachment.applicationNumber +
                attachment.fileName.split('.').pop(),
              res.body as Blob
            );
          },
          error: () => {
            this.showErrorToastMessage();
          },
        })
    );
  }

  applicationCertificates(application: Record<string, any> | undefined): any {
    if (!application || !application['officerAttachments']) return [];

    return Object.values(application['officerAttachments']).flat();
  }

  editApplication() {
    // If logged in or OTP already verified, proceed immediately
    if (this.isLoggedIn || this.otp !== '') {
      this.editApplicationEvent.emit(this.otp);
      return;
    }

    // OTP not yet verified — trigger OTP flow, then edit after verification
    this.pendingEditAfterOtp = true;

    if (this.isLegacyApplication && this.gov2SummaryId) {
      this.handleGov2ViewMoreDetails(this.tokenModal);
    } else {
      this.handleViewMoreDetails(this.tokenModal);
    }
  }

  getApplicationStatusToLabel(applicationStatus: string): string {
    return getApplicationStateLabel(applicationStatus);
  }

  /**
   * Get the feedback details that match the state tracker based on timestamp
   * @param tracker The state tracker to find feedback for
   * @returns Array of feedback response details or null if none found
   */
  getFeedbackForTracker(tracker: any): IFeedbackResponseDetails[] | null {
    if (
      !this.applicationDetails?.['applicationFeedbacks']
        ?.feedbackResponseDetails
    ) {
      return null;
    }

    // For rejected applications, find feedback that matches the tracker's timestamp
    if (tracker.applicationEvent === 'REJECT') {
      const feedbacks =
        this.applicationDetails?.['applicationFeedbacks']
          ?.feedbackResponseDetails;

      // Compare feedbackTime with stateTimestamp (allow for slight time differences)
      const matchingFeedbacks = feedbacks.filter(
        (feedback: IFeedbackResponseDetails) => {
          // Check if applicationEvent matches
          const eventMatches =
            feedback.applicationEvent === tracker.applicationEvent;

          // Compare feedbackTime with stateTimestamp (allow for slight time differences)
          const feedbackTime = new Date(feedback.feedbackTime).getTime();
          const trackerTime = new Date(tracker.stateTimestamp).getTime();

          // Allow a small time difference (10 seconds) for potential timestamp precision differences
          const timeDifference = Math.abs(feedbackTime - trackerTime);
          const maxTimeDifference = 10 * 1000; // 10 seconds in milliseconds
          const timeMatches = timeDifference <= maxTimeDifference;

          // Return true only if both conditions are met
          return eventMatches && timeMatches;
        }
      );

      return matchingFeedbacks.length > 0 ? matchingFeedbacks : null;
    }

    return null;
  }

  getActiveFeedback(
    feedBack: IFeedbackResponseDetails[],
    feedbackTime: string
  ): IFeedbackResponseDetails[] {
    // Convert feedbackTime to seconds by removing microseconds
    const targetTime = new Date(feedbackTime).toISOString().split('.')[0];

    return feedBack.filter(x => {
      if (!x.activeFeedback) return false;

      // Convert feedback's time to seconds for comparison
      const feedbackTimeInSeconds = new Date(x.feedbackTime)
        .toISOString()
        .split('.')[0];
      return feedbackTimeInSeconds === targetTime;
    });
  }

  convertDate(dateString: string): Date {
    // Split the string into date and time components using 'T'
    const [datePart, timePart] = dateString.split('T');
    const [day, month, year] = datePart.split('/').map(Number);
    const [hours = 0, minutes = 0, seconds = 0] = timePart
      ? timePart.split(':').map(Number)
      : [0, 0, 0];
    return new Date(Date.UTC(year, month - 1, day, hours, minutes, seconds));
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
    this.countdownSubscription?.unsubscribe();
  }

  // ==================== Gov2 (Legacy) Methods ====================

  // Flag to track if current OTP flow is for Gov2
  private isGov2OtpFlow = false;
  private pendingEditAfterOtp = false;

  /**
   * Handle View More Details for Gov2 applications
   * Now uses OTP flow same as Hub applications
   */
  handleGov2ViewMoreDetails(content: TemplateRef<unknown>): void {
    if (!this.gov2SummaryId) {
      this.ts.show({
        body: 'Unable to load application details',
        type: 'error',
        delay: 5000,
      });
      return;
    }

    this.isGov2OtpFlow = true;
    this.requestingAccess = true;

    this.gov2SummaryService
      .requestAccess(this.application.applicationNumber, this.locale)
      .subscribe({
        next: response => {
          this.requestingAccess = false;
          if (response.responseCode === 'APPLICATION_OTP_NOT_REQUIRED') {
            // No OTP required - load summary directly
            this.viewMoreDetails = true;
            this.viewFullDetails = true;
            this.loadGov2Summary('');
            // If edit was pending, OTP is not required — proceed
            if (this.pendingEditAfterOtp) {
              this.pendingEditAfterOtp = false;
              this.editApplicationEvent.emit('');
            }
          } else if (response.status && response.data) {
            // OTP sent - show modal
            this.openOTPModal(content, response.data as IRequestAccessResponse);
          } else {
            this.ts.show({
              body: this.extractErrorMessage(response),
              type: 'error',
              delay: 5000,
            });
          }
        },
        error: err => {
          this.requestingAccess = false;
          this.isGov2OtpFlow = false;
          this.ts.show({
            body: this.extractErrorMessage(err),
            type: 'error',
            delay: 5000,
          });
        },
      });
  }

  /**
   * Confirm OTP for Gov2 applications
   */
  confirmGov2Otp(ngOtpInputRef: NgOtpInputComponent): void {
    if (this.otp.length !== 5) return;

    this.isConfirming = true;
    this.loadGov2Summary(this.otp, ngOtpInputRef);
  }

  /**
   * Resend OTP for Gov2 applications
   */
  resendGov2OTP(): void {
    this.resendingToken = true;
    this.gov2SummaryService
      .requestAccess(this.application.applicationNumber, this.locale)
      .subscribe({
        next: () => {
          this.resendingToken = false;
          this.restartCountdown();
        },
        error: err => {
          this.resendingToken = false;
          this.ts.show({
            body: this.extractErrorMessage(err),
            type: 'error',
            delay: 5000,
          });
        },
      });
  }

  private extractErrorMessage(payload: any, fallbackKey?: string): string {
    return extractBackendErrorMessage(
      payload,
      (key: string) => this.translateService.instant(key),
      fallbackKey ? { fallbackKey } : undefined
    );
  }

  /**
   * Load Gov2 application summary via Hub proxy
   * @param otp - The OTP to verify (empty string if not required)
   * @param ngOtpInputRef - Optional OTP input reference for error handling
   */
  private loadGov2Summary(
    otp: string,
    ngOtpInputRef?: NgOtpInputComponent
  ): void {
    if (!this.gov2SummaryId) {
      this.ts.show({
        body: 'Unable to load application details',
        type: 'error',
        delay: 5000,
      });
      return;
    }

    this.isLoadingGov2Summary = true;
    this.gov2SummaryLoaded = false;

    this.gov2SummaryService
      .getApplicationSummary(
        this.application.applicationNumber,
        this.gov2SummaryId,
        otp
      )
      .subscribe({
        next: response => {
          this.isLoadingGov2Summary = false;
          this.isConfirming = false;

          if (response.status && response.data) {
            this.modalService.dismissAll();
            this.gov2Summary = response.data.data?.summary || [];
            this.gov2Documents = response.data.data?.documents || [];
            this.gov2SummaryLoaded = true;
            this.viewMoreDetails = true;
            this.viewFullDetails = true;
            this.isGov2OtpFlow = false;

            // If edit was pending OTP verification, emit now
            if (this.pendingEditAfterOtp) {
              this.pendingEditAfterOtp = false;
              this.editApplicationEvent.emit(this.otp);
            }
          } else {
            // Check if it's an OTP error
            if (
              response.responseCode === 'GOV2_OTP_INVALID' ||
              response.message?.includes('OTP') ||
              response.message?.includes('Invalid')
            ) {
              if (ngOtpInputRef) {
                this.markControlsAsInValid(ngOtpInputRef);
              }
              this.ts.show({
                body: this.extractErrorMessage(response, 'GOV2_OTP_INVALID'),
                type: 'error',
                delay: 5000,
              });
            } else {
              this.ts.show({
                body: this.extractErrorMessage(response),
                type: 'error',
                delay: 5000,
              });
            }
          }
        },
        error: err => {
          this.isLoadingGov2Summary = false;
          this.isConfirming = false;

          // Check if it's an OTP verification error
          if (err?.status === 400 && ngOtpInputRef) {
            this.markControlsAsInValid(ngOtpInputRef);
          }

          this.ts.show({
            body: this.extractErrorMessage(err),
            type: 'error',
            delay: 5000,
          });
          console.error('Failed to load Gov2 summary:', err);
        },
      });
  }

  /**
   * Check if current OTP flow is for Gov2
   */
  isGov2Flow(): boolean {
    return this.isGov2OtpFlow;
  }

  getLocaleIcon(locale: string): string {
    return getUnicodeFlagIcon(
      locale === 'en'
        ? 'GB'
        : locale.includes('-')
        ? locale.split('-')[1]
        : locale
    );
  }

  getLocaleNameByCode(code: string) {
    if (
      this.supportedLocales.filter(element => element.locale === code).length >
      0
    ) {
      return this.supportedLocales.filter(element => element.locale === code)[0]
        .name;
    }
    return code;
  }

  getUnifiedCertificates(): IUnifiedDocument[] {
    const items: IUnifiedDocument[] = [];
    const sysCert = this.applicationDetails?.['applicationCertificate'];
    const officerAttachments = this.applicationDetails?.['officerAttachments'];

    if (sysCert && sysCert.certificateNames) {
      Object.keys(sysCert.certificateNames).forEach(locale => {
        items.push({
          label: sysCert.certificateName
            ? sysCert.certificateName
            : this.getLocaleNameByCode(locale),
          localeFlag: this.getLocaleIcon(locale),
          locale: locale,
          isOfficerAttachment: false,
        });
      });
    }

    // For Gov 2.0 legacy applications, use certificateLocales from federated response
    if (
      this.isLegacyApplication &&
      items.length === 0 &&
      this.federatedApplication?.certificateLocales
    ) {
      this.federatedApplication.certificateLocales.forEach(lang => {
        items.push({
          label: lang,
          locale: lang,
          isOfficerAttachment: false,
        });
      });
    }

    if (officerAttachments) {
      Object.values(officerAttachments)
        .flat()
        .forEach((attachment: any) => {
          items.push({
            label: attachment.attachmentName,
            isOfficerAttachment: true,
            rawAttachment: attachment,
          });
        });
    }

    return items;
  }

  handleUnifiedDownload(item: IUnifiedDocument) {
    if (this.downloadingCert) {
      return;
    }
    this.downloadingCert = true;
    this.closeCertificateDropdowns();
    if (item.isOfficerAttachment && item.rawAttachment) {
      this.downloadAttachmentByOtp(item.rawAttachment);
    } else if (!item.isOfficerAttachment && item.locale) {
      this.downloadCertificate(item.locale);
    }
  }

  private closeCertificateDropdowns(): void {
    this.certificateDropdowns?.forEach(dropdown => {
      const el = dropdown.nativeElement as HTMLElement;
      el.classList.remove('show');
      el.setAttribute('aria-expanded', 'false');
      const menu = el.nextElementSibling;
      if (menu) {
        menu.classList.remove('show');
      }
    });
  }

  getReceiptSupportedLocales() {
    this.localeService
      .getSupportedLocales()
      .pipe(map(res => res.data.content))
      .subscribe({
        next: response => {
          this.receiptSupportedLocales = response;
        },
        error: (error: any) => {
          console.error('Failed to load locales for receipts', error);
        },
      });
  }
}
